import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Upload, RefreshCw } from "lucide-react";
import {
    PlaceImportFile,
    PlaceImportStats,
    PlaceSourceKey,
    PlaceSourcesStatus,
    getPlaceSourcesStatus,
    uploadPlaceFiles,
} from "../../../interfaces/placeSources";

// /bo/import-lieux — upload the OpenStreetMap / DATAtourisme files built on a computer by
// `scripts/place sources/exportPlaceSources.py`. The server only matches and writes, in the
// background and in small paced batches (crm-back services/placeSources/sync.ts); this page polls
// its progress. Same pattern as /bo/import-etablissements for SIRENE.

const SOURCE_LABEL: Record<PlaceSourceKey, string> = { osm: "OpenStreetMap", datatourisme: "DATAtourisme" };
const PHASE_LABEL: Record<PlaceImportFile["status"], string> = {
    queued: "En attente",
    matching: "1/3 · Rapprochement avec les établissements",
    resolving: "2/3 · Recherche des événements",
    writing: "3/3 · Écriture et approbation",
    done: "Terminé",
    error: "Erreur",
};
const fmt = (n?: number) => (n ?? 0).toLocaleString("fr-FR");
const when = (d?: string) => (d ? new Date(d).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

function StatsLine({ stats }: { stats: PlaceImportStats }) {
    return (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs text-gray-600">
            <span>Lieux lus : <b className="text-gray-900">{fmt(stats.records)}</b></span>
            <span>Reliés : <b className="text-gray-900">{fmt(stats.matched)}</b></span>
            <span title="Lien sûr (SIRET, même nom à la même adresse)">« high » : <b className="text-gray-900">{fmt(stats.high)}</b></span>
            <span title="Notés ensuite par l'IA, comme les fiches Google">À noter par l'IA : <b className="text-gray-900">{fmt(stats.unverified)}</b></span>
            <span>Approuvés auto : <b className="text-gray-900">{fmt(stats.approved)}</b></span>
            <span title="Même lieu, même date de modification qu'au dernier import">Inchangés : <b className="text-gray-900">{fmt(stats.unchanged)}</b></span>
            <span>Non reliés (pool) : <b className="text-gray-900">{fmt(stats.pooled)}</b></span>
            <span title="Établissements sans événement : créé pour porter l'enrichissement">Événements créés : <b className="text-gray-900">{fmt(stats.eventsCreated)}</b></span>
        </div>
    );
}

function FileProgress({ file }: { file: PlaceImportFile }) {
    const pct = file.total ? Math.min(100, Math.round((100 * file.done) / file.total)) : null;
    const active = ["matching", "resolving", "writing"].includes(file.status);
    return (
        <div className="border border-gray-200 rounded-xs p-3 flex flex-col gap-2">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <span className="font-medium text-gray-900">
                    {file.source ? SOURCE_LABEL[file.source] : "Fichier"} · <span className="text-gray-500 font-normal">{file.name}</span>
                </span>
                <span className={file.status === "error" ? "text-red-600" : file.status === "done" ? "text-green-700" : "text-gray-600"}>
                    {PHASE_LABEL[file.status]}
                    {active && pct !== null && file.status !== "resolving" && ` — ${fmt(file.done)} / ${fmt(file.total)}`}
                </span>
            </div>
            {active && (
                <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                    <div
                        className={`h-full rounded-full bg-black ${file.status === "resolving" || pct === null ? "animate-pulse w-full opacity-30" : ""}`}
                        style={file.status !== "resolving" && pct !== null ? { width: `${pct}%` } : undefined}
                    />
                </div>
            )}
            {file.error && <p className="text-xs text-red-600">{file.error}</p>}
            <StatsLine stats={file.stats} />
            {file.meta?.notes?.length ? <p className="text-xs text-gray-400">Script : {file.meta.notes.join(" · ")}</p> : null}
        </div>
    );
}

export default function ImportPlaces() {
    const [status, setStatus] = useState<PlaceSourcesStatus | null>(null);
    const [files, setFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [uploadPct, setUploadPct] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const load = () =>
        getPlaceSourcesStatus()
            .then(setStatus)
            .catch(() => setError("Impossible de lire l'état des imports."));

    useEffect(() => {
        load();
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, []);

    // Poll while an import runs.
    const running = !!status?.import?.running;
    useEffect(() => {
        if (running && !pollRef.current) pollRef.current = setInterval(load, 3000);
        if (!running && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, [running]);

    const pick = (list: FileList | null) => {
        setError(null);
        const picked = Array.from(list || []).filter((f) => /\.jsonl\.gz$/i.test(f.name)).slice(0, 2);
        if (list && list.length && !picked.length) setError("Choisissez les fichiers .jsonl.gz produits par le script.");
        setFiles(picked);
    };

    const start = () => {
        if (!files.length) return;
        setError(null);
        setUploadPct(0);
        uploadPlaceFiles(files, setUploadPct)
            .then(() => {
                setFiles([]);
                return load();
            })
            .catch((e) => setError(e?.response?.data?.error ?? "L'envoi a échoué."))
            .finally(() => setUploadPct(null));
    };

    const busy = running || uploadPct !== null;

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-5xl px-6 py-6 flex flex-col gap-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">Import des lieux (OpenStreetMap / DATAtourisme)</h1>
                        <p className="text-sm text-gray-600 mt-1">
                            Téléphones, sites, emails, horaires et réseaux sociaux tirés des données ouvertes, reliés aux établissements et vérifiés comme les fiches Google.
                        </p>
                    </div>
                    <button onClick={load} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">
                        <RefreshCw className={`w-4 h-4 ${running ? "animate-spin" : ""}`} /> Actualiser
                    </button>
                </div>

                <div className="bg-white border border-gray-200 rounded-xs p-4 text-xs text-gray-700 flex flex-col gap-1.5">
                    <p className="font-semibold text-gray-900 text-sm">1. Sur votre ordinateur</p>
                    <p>
                        Dans le dossier <code className="bg-gray-100 px-1 rounded">scripts/place sources</code>, lancez{" "}
                        <code className="bg-gray-100 px-1 rounded">python exportPlaceSources.py</code> (la première fois :{" "}
                        <code className="bg-gray-100 px-1 rounded">pip install osmium</code>). Il télécharge l'extrait OpenStreetMap de Geofabrik (~5 Go, repris en cas de coupure et
                        vérifié) et DATAtourisme, fait tout le travail lourd et écrit deux fichiers dans <code className="bg-gray-100 px-1 rounded">export/</code> :{" "}
                        <code className="bg-gray-100 px-1 rounded">osm-places.jsonl.gz</code> et <code className="bg-gray-100 px-1 rounded">datatourisme-places.jsonl.gz</code>.
                    </p>
                    <p className="text-gray-500">
                        Si le téléchargement est interrompu, relancez simplement la même commande : il reprend là où il s'était arrêté.
                    </p>
                    <p className="font-semibold text-gray-900 text-sm mt-2">2. Ici</p>
                    <p>
                        Déposez un ou les deux fichiers. Le serveur les relie aux établissements par lots, avec des pauses, sans rien télécharger lui-même. Les liens sûrs sont
                        approuvés si <code className="bg-gray-100 px-1 rounded">autoApprove:place_sources</code> est activé dans /bo/services ; les autres sont notés par l'IA puis
                        relus dans <Link to="/bo/enrichments" className="underline">/bo/enrichments</Link>. Les lieux sans établissement vont dans{" "}
                        <Link to="/bo/unmatched-places" className="underline">Lieux non reliés</Link>.
                    </p>
                </div>

                <div className="bg-white border border-gray-200 rounded-xs p-4">
                    <div
                        className={`rounded-xs border-2 border-dashed p-8 text-center transition-colors ${isDragging ? "border-gray-700 bg-gray-50" : "border-gray-200"}`}
                        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
                        onDrop={(e) => {
                            e.preventDefault(); e.stopPropagation(); setIsDragging(false);
                            pick(e.dataTransfer.files);
                        }}
                    >
                        <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                            <Upload className="h-5 w-5 text-gray-600" />
                        </div>
                        <p className="text-sm font-medium text-gray-900">Glissez-déposez les fichiers .jsonl.gz ici (un ou deux)</p>
                        <p className="text-xs text-gray-500 mt-1">ou</p>
                        <label className="mt-3 inline-flex cursor-pointer items-center justify-center rounded-xs border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-900 hover:bg-[#F9F6F3]">
                            Choisir les fichiers
                            <input type="file" accept=".gz,application/gzip" multiple className="hidden" onChange={(e) => pick(e.target.files)} />
                        </label>
                        <div className="mt-3 flex flex-col items-center gap-1 text-xs text-gray-600">
                            {files.length === 0 && <span className="text-gray-400">Aucun fichier choisi</span>}
                            {files.map((f) => (
                                <span key={f.name} className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-gray-400" /> {f.name} · {(f.size / 1024 / 1024).toFixed(1)} Mo
                                </span>
                            ))}
                        </div>
                        <div className="mt-4">
                            <button
                                onClick={start}
                                disabled={!files.length || busy}
                                className={`rounded-xs px-4 py-2 text-xs font-semibold text-white ${!files.length || busy ? "cursor-not-allowed bg-gray-300" : "bg-black hover:bg-gray-900"}`}
                            >
                                {uploadPct !== null ? `Envoi… ${uploadPct} %` : running ? "Import en cours…" : "Lancer l'import"}
                            </button>
                        </div>
                        {error && <p className="mt-3 text-xs text-red-600">{error}</p>}
                    </div>
                </div>

                {status?.import && (
                    <div className="bg-white border border-gray-200 rounded-xs p-4 flex flex-col gap-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-semibold text-gray-900">{running ? "Import en cours" : "Dernier import"}</p>
                            <p className="text-xs text-gray-500">
                                Démarré le {when(status.import.startedAt)}
                                {status.import.finishedAt && ` · terminé le ${when(status.import.finishedAt)}`}
                            </p>
                        </div>
                        {status.import.files.map((f) => (
                            <FileProgress key={f.name} file={f} />
                        ))}
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {(["osm", "datatourisme"] as PlaceSourceKey[]).map((s) => {
                        const src = status?.sources?.[s];
                        return (
                            <div key={s} className="bg-white border border-gray-200 rounded-xs p-3 flex flex-col gap-2 text-xs">
                                <span className="text-sm font-semibold text-gray-900">{SOURCE_LABEL[s]}</span>
                                {src?.last ? (
                                    <>
                                        <span className="text-gray-500">
                                            Dernier import le {when(src.last.at)} · fichier du {when(src.last.generatedAt)}
                                            {src.last.complete === false && " · fichier incomplet (zones manquantes)"}
                                        </span>
                                        <StatsLine stats={src.last.stats} />
                                    </>
                                ) : (
                                    <span className="text-gray-400">Jamais importé</span>
                                )}
                                <Link to="/bo/unmatched-places" className="text-gray-600 underline">
                                    {fmt(src?.pool.unmatched)} lieux non reliés à revoir
                                </Link>
                            </div>
                        );
                    })}
                </div>

                <p className="text-xs text-gray-400">Données OpenStreetMap © les contributeurs OpenStreetMap (ODbL) · DATAtourisme sous Licence Ouverte 2.0.</p>
            </div>
        </div>
    );
}
